import React, { useState } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { Registration } from '../../types/registration';
import './RefundTool.css';

const RefundTool: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState<'name' | 'email'>('name');
  const [loading, setLoading] = useState(false);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [selectedRegistration, setSelectedRegistration] = useState<Registration | null>(null);
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [sendEmail, setSendEmail] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setError('Please enter a search term');
      return;
    }

    setLoading(true);
    setError(null);
    setRegistrations([]);
    setSelectedRegistration(null);

    try {
      let q;
      if (searchType === 'email') {
        q = query(collection(db, 'Registrations'), where('email', '==', searchTerm.trim()));
      } else {
        // Search by name - need to search in firstName and lastName
        // Firestore doesn't support OR queries easily, so we'll search both
        const nameQuery = query(
          collection(db, 'Registrations'),
          where('firstName', '==', searchTerm.trim().split(' ')[0])
        );
        const snapshot = await getDocs(nameQuery);
        const results: Registration[] = [];
        snapshot.forEach((doc) => {
          const data = { id: doc.id, ...doc.data() } as Registration;
          const fullName = `${data.firstName} ${data.lastName}`.toLowerCase();
          if (fullName.includes(searchTerm.toLowerCase())) {
            results.push(data);
          }
        });
        setRegistrations(results);
        setLoading(false);
        return;
      }

      const snapshot = await getDocs(q);
      const results: Registration[] = [];
      snapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() } as Registration);
      });
      setRegistrations(results);
    } catch (err: any) {
      console.error('Error searching:', err);
      setError(err.message || 'Failed to search');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRegistration = (reg: Registration) => {
    setSelectedRegistration(reg);
    setRefundAmount('');
    setError(null);
    setSuccess(null);
  };

  const handleRefund = async () => {
    if (!selectedRegistration) {
      setError('Please select a registration');
      return;
    }

    const paymentHistory = selectedRegistration.paymentHistory || [];
    const creditCardPayments = paymentHistory.filter((p) => p.method === 'CreditCard' && (Number(p.amount) || 0) > 0);

    if (creditCardPayments.length === 0) {
      setError('No credit card payments found for this registration');
      return;
    }

    const amount = refundAmount ? parseFloat(refundAmount) : null;
    if (amount !== null && (isNaN(amount) || amount <= 0)) {
      setError('Please enter a valid refund amount');
      return;
    }

    // Find the payment to refund
    const paymentToRefund = creditCardPayments[0]; // For now, refund the first payment
    if (!paymentToRefund.stripePaymentId) {
      setError('Payment does not have a Stripe payment ID');
      return;
    }

    setProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      const processStripeRefund = httpsCallable(functions, 'processStripeRefund');
      const result = await processStripeRefund({
        paymentId: paymentToRefund.id,
        registrationId: selectedRegistration.id,
        refundAmount: amount,
        sendEmail,
      });

      setSuccess(`Refund processed successfully. Refund ID: ${(result.data as any).refundId}`);
      
      // Refresh registration data
      const regDoc = await getDoc(doc(db, 'Registrations', selectedRegistration.id));
      if (regDoc.exists()) {
        setSelectedRegistration({ id: regDoc.id, ...regDoc.data() } as Registration);
      }
      
      setRefundAmount('');
    } catch (err: any) {
      console.error('Error processing refund:', err);
      setError(err.message || 'Failed to process refund');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="refund-tool">
      <h1>Stripe Refund Tool</h1>

      <div className="search-section">
        <div className="search-controls">
          <select
            value={searchType}
            onChange={(e) => setSearchType(e.target.value as 'name' | 'email')}
            className="form-control"
          >
            <option value="name">Search by Name</option>
            <option value="email">Search by Email</option>
          </select>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={searchType === 'email' ? 'Enter email address' : 'Enter camper name'}
            className="form-control"
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch} disabled={loading} className="btn btn-primary">
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {registrations.length > 0 && (
        <div className="registrations-list">
          <h2>Found {registrations.length} registration(s)</h2>
          {registrations.map((reg) => (
            <div
              key={reg.id}
              className={`registration-item ${selectedRegistration?.id === reg.id ? 'selected' : ''}`}
              onClick={() => handleSelectRegistration(reg)}
            >
              <div>
                <strong>{reg.firstName} {reg.lastName}</strong> - {reg.email}
              </div>
              <div>
                <small>Registration ID: {reg.id}</small>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedRegistration && (
        <div className="refund-section">
          <h2>Process Refund</h2>
          <div className="registration-details">
            <p><strong>Camper:</strong> {selectedRegistration.firstName} {selectedRegistration.lastName}</p>
            <p><strong>Email:</strong> {selectedRegistration.email}</p>
            <p><strong>Registration ID:</strong> {selectedRegistration.id}</p>
          </div>

          <div className="payments-list">
            <h3>Credit Card Payments</h3>
            {selectedRegistration.paymentHistory
              ?.filter((p) => p.method === 'CreditCard' && (Number(p.amount) || 0) > 0)
              .map((payment, index) => (
                <div key={payment.id || index} className="payment-item">
                  <div>
                    <strong>${(Number(payment.amount) || 0).toFixed(2)}</strong>
                    {payment.convenienceFee && (
                      <span> (Fee: ${payment.convenienceFee.toFixed(2)})</span>
                    )}
                  </div>
                  <div>
                    <small>{(() => {
                      const d = payment.date as any;
                      if (d instanceof Timestamp) return d.toDate().toLocaleDateString();
                      if (d && typeof d.toDate === 'function') return d.toDate().toLocaleDateString();
                      return new Date(d).toLocaleDateString();
                    })()}</small>
                  </div>
                  {payment.stripePaymentId && (
                    <div>
                      <small>Stripe ID: {payment.stripePaymentId}</small>
                    </div>
                  )}
                </div>
              ))}
          </div>

          <div className="refund-form">
            <div className="form-group">
              <label htmlFor="refundAmount">Refund Amount (leave empty for full refund)</label>
              <input
                type="number"
                id="refundAmount"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder="Enter amount or leave empty for full refund"
                className="form-control"
                step="0.01"
                min="0"
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                />
                {' '}Send email notification (default: checked)
              </label>
            </div>

            <button
              onClick={handleRefund}
              disabled={processing}
              className="btn btn-danger"
            >
              {processing ? 'Processing Refund...' : 'Process Refund'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundTool;

